/** Auth store with Pinia */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import axios, { getAccessToken } from '@/api/axios'
import { startHeartbeat, stopHeartbeat } from '@/api/heartbeat'
import { startActivityTracking, stopActivityTracking } from '@/api/activity'
import { useToast } from '@/composables/useToast'
import { usePermissionsStore } from '@/stores/permissions'
import { v, assertValid } from '@/utils/validateStore'

// API URLs are now hardcoded to relative paths for nginx proxy

interface User {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  full_name: string | null
  role: string | null
  role_id: number | null
  is_active: boolean
  last_login: string | null
}

interface TokenData {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

const userSchema = v.shape({
  id: v.number,
  email: v.string,
  first_name: v.nullable(v.string),
  last_name: v.nullable(v.string),
  full_name: v.nullable(v.string),
  role: v.nullable(v.string),
  role_id: v.nullable(v.number),
  is_active: v.boolean,
  last_login: v.nullable(v.string),
})

const tokenDataSchema = v.shape({
  access_token: v.string,
  refresh_token: v.string,
  token_type: v.string,
  expires_in: v.number,
})

export const useAuthStore = defineStore(
  'auth',
  () => {
    const AUTH_STORAGE_KEY = 'cti-auth'
    const toast = useToast()
    const router = useRouter()

    // State
    const user = ref<User | null>(null)
    const token = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const loading = ref(false)

    // Getters
    // Only depend on token - user is loaded async
    const isAuthenticated = computed(() => !!token.value)
    // Legacy role helpers — deprecated. Use permissionsStore.can('...') instead.
    // These rely on role_id mapping since the backend no longer returns string roles.
    const isAdmin = computed(() => user.value?.role_id === 1)
    const isManager = computed(() => [1, 2].includes(user.value?.role_id ?? 0))
    const isOperator = computed(() => user.value?.role_id === 4)
    const isGenerator = computed(() => user.value?.role_id === 3)
    const userFullName = computed(() => user.value?.full_name || user.value?.email)

    function clearAuthState() {
      stopHeartbeat()
      stopActivityTracking()
      user.value = null
      token.value = null
      refreshToken.value = null
      localStorage.removeItem('token')
      localStorage.removeItem(AUTH_STORAGE_KEY)

      // Clear permissions cache on logout
      const permissionsStore = usePermissionsStore()
      permissionsStore.clear()
    }

    // Actions
    async function login(email: string, password: string) {
      loading.value = true
      try {
        const formData = new URLSearchParams()
        formData.append('username', email)
        formData.append('password', password)

        const response = await axios.post<TokenData>(`/api/v1/auth/login`, formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })

        assertValid('auth.login.tokenData', response.data, tokenDataSchema)
        token.value = response.data.access_token
        refreshToken.value = response.data.refresh_token

        // Store token in localStorage for axios interceptor
        localStorage.setItem('token', response.data.access_token)

        // Start heartbeat so the backend marks this operator as online.
        startHeartbeat()

        // Start activity tracking (clicks/keys) to keep presence fresh.
        startActivityTracking()

        // Fetch user profile
        const profile = await fetchProfile()

        if (!profile) {
          throw new Error('No se pudo obtener el perfil del usuario')
        }

        // Hydrate permissions after successful login
        try {
          const permissionsStore = usePermissionsStore()
          await permissionsStore.fetchPermissions()
        } catch {
          // Si falla el fetch de permisos, no bloqueamos el login.
          // Se seguirá intentando en navegación y el guard puede redirigir si es necesario.
        }

        // Redirect basado en permisos granulares usando window.location
        // para evitar dependencia circular entre auth y router
        const permissionsStore = usePermissionsStore()
        let target = '/'
        if (permissionsStore.can('dashboard:view_supervisor')) {
          target = '/dashboard/supervisor'
        } else if (permissionsStore.can('generator:view_own_leads')) {
          target = '/generator/dashboard'
        } else if (permissionsStore.can('dashboard:view_operator')) {
          target = '/dashboard/operator'
        }
        window.location.href = target

        toast.success('Inicio de sesión exitoso')
        return true
      } catch (error: any) {
        clearAuthState()
        const message = error.response?.data?.detail || error.message || 'Error al iniciar sesión'
        toast.error(message)
        return false
      } finally {
        loading.value = false
      }
    }

    async function fetchProfile() {
      try {
        const response = await axios.get<User>(`/api/v1/auth/me`)
        assertValid('auth.fetchProfile', response.data, userSchema)
        user.value = response.data
        return response.data
      } catch (error: any) {
        const message = error.response?.data?.detail || 'Error al obtener perfil'
        toast.error(message)
        return null
      }
    }

    async function refreshAccessToken() {
      try {
        if (!refreshToken.value) {
          throw new Error('No refresh token available')
        }

        const response = await axios.post<TokenData>(`/api/v1/auth/refresh`, null, {
          headers: {
            Authorization: `Bearer ${refreshToken.value}`,
          },
        })

        assertValid('auth.refreshAccessToken', response.data, tokenDataSchema)

        token.value = response.data.access_token
        refreshToken.value = response.data.refresh_token
        localStorage.setItem('token', response.data.access_token)

        return true
      } catch (error) {
        clearAuthState()
        return false
      }
    }

    async function changePassword(currentPassword: string, newPassword: string) {
      try {
        await axios.post(`/api/v1/auth/password/change`, {
          current_password: currentPassword,
          new_password: newPassword,
        })
        toast.success('Contraseña actualizada exitosamente')
        return true
      } catch (error: any) {
        const message = error.response?.data?.detail || 'Error al cambiar contraseña'
        toast.error(message)
        return false
      }
    }

    async function forgotPassword(email: string) {
      try {
        await axios.post(`/api/v1/auth/password/forgot`, null, {
          params: { email },
        })
        toast.success('Si el email existe, recibirás instrucciones para restablecer tu contraseña')
        return true
      } catch (error: any) {
        const message = error.response?.data?.detail || 'Error al solicitar recuperación'
        toast.error(message)
        return false
      }
    }

    async function loginWithOAuth(provider: string) {
      try {
        const redirectUri = `${window.location.origin}/oauth/callback`

        // Get authorization URL from backend
        const response = await axios.get(`/api/v1/auth/oauth/authorize`, {
          params: { provider, redirect_uri: redirectUri },
        })

        const authUrl = response.data.authorization_url
        if (!authUrl) {
          toast.error('OAuth no configurado para este proveedor')
          return false
        }

        // Open OAuth popup
        const popup = window.open(
          authUrl,
          'oauth',
          'width=500,height=600,scrollbars=yes'
        )

        if (!popup) {
          toast.error('Por favor permite ventanas emergentes para login con OAuth')
          return false
        }

        // Listen for OAuth callback message
        return new Promise((resolve) => {
          let resolved = false
          let checkClosed = 0 as unknown as number

          const finish = (result: boolean) => {
            if (resolved) return
            resolved = true
            window.removeEventListener('message', handleMessage)
            clearInterval(checkClosed)
            resolve(result)
          }

          const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return

            if (event.data?.type === 'oauth-success') {
              // Set tokens
              token.value = event.data.access_token
              refreshToken.value = event.data.refresh_token
              localStorage.setItem('token', event.data.access_token)

              // Fetch profile
              fetchProfile().then(async () => {
                try {
                  const permissionsStore = usePermissionsStore()
                  await permissionsStore.fetchPermissions()
                } catch {
                  // No bloqueamos el login si falla la carga de permisos
                }

                // Start heartbeat for the OAuth session as well.
                startHeartbeat()

                // Start activity tracking for OAuth session.
                startActivityTracking()

                toast.success('Inicio de sesión exitoso')
                // Redirect based on permissions (RBAC v2)
                const permissionsStore = usePermissionsStore()
                if (permissionsStore.can('dashboard:view_supervisor')) {
                  router.push('/dashboard/supervisor')
                } else if (permissionsStore.can('generator:view_own_leads')) {
                  window.location.href = '/generator/dashboard'
                } else if (permissionsStore.can('dashboard:view_operator')) {
                  window.location.href = '/dashboard/operator'
                } else {
                  window.location.href = '/'
                }
              })

              finish(true)
            } else if (event.data?.type === 'oauth-error') {
              toast.error(event.data.message || 'Error en login OAuth')
              finish(false)
            }
          }

          window.addEventListener('message', handleMessage)

          // Cleanup if popup closed manually
          checkClosed = window.setInterval(() => {
            if (popup.closed) {
              finish(false)
            }
          }, 1000)
        })
      } catch (error: any) {
        const message = error.response?.data?.detail || 'Error en login OAuth'
        toast.error(message)
        return false
      }
    }

    async function resetPassword(token: string, newPassword: string) {
      try {
        await axios.post(`/api/v1/auth/password/reset`, null, {
          params: {
            token,
            new_password: newPassword,
          },
        })
        toast.success('Contraseña restablecida exitosamente. Por favor, inicia sesión.')
        return true
      } catch (error: any) {
        const message = error.response?.data?.detail || 'Error al restablecer contraseña'
        toast.error(message)
        return false
      }
    }

    // Flag to avoid duplicating the sendBeacon call when both logout()
    // and the pagehide listener fire during the same page lifecycle.
    let logoutBeaconSent = false

    function logout() {
      const tokenValue = token.value || getAccessToken()

      if (tokenValue && !logoutBeaconSent) {
        logoutBeaconSent = true
        try {
          sessionStorage.setItem('cti-logout-sent', '1')
        } catch {
          // Ignore storage errors in private mode.
        }

        const url = `/api/v1/auth/logout?token=${encodeURIComponent(tokenValue)}`

        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(url)
        } else {
          axios.post(url).catch(() => {})
        }
      }

      clearAuthState()
      router.push('/login')
      toast.success('Sesión cerrada')
    }

    // Ensure the backend is notified when the tab/browser closes without
    // pressing the logout button. sendBeacon survives pagehide/unload.
    function handlePageHide(event: PageTransitionEvent) {
      // Respect a sessionStorage sentinel written by a previous explicit logout
      // so the back-forward cache does not emit a stale logout on restore.
      try {
        if (sessionStorage.getItem('cti-logout-sent')) {
          return
        }
      } catch {
        // Ignore storage errors in private mode.
      }

      const tokenValue = token.value || getAccessToken()
      if (!tokenValue || logoutBeaconSent) {
        return
      }

      logoutBeaconSent = true
      const url = `/api/v1/auth/logout?token=${encodeURIComponent(tokenValue)}&source=window_close`

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const beaconSent = navigator.sendBeacon(url)
        if (!beaconSent && event.type === 'pagehide' && (event as PageTransitionEvent).persisted) {
          // If the page was kept in the back-forward cache and the beacon failed,
          // the heartbeat will naturally stop and the backend job will mark offline.
        }
      }
    }

    if (typeof window !== 'undefined') {
      // NOTA: desactivado el logout automático por 'pagehide' (source=window_close).
      // El evento 'pagehide' también se dispara al RECARGAR la página (F5) o al
      // navegar, no solo al cerrar la ventana, lo que provocaba que un operador
      // quedara 'offline' en cada recarga y luego reviviera a 'available'
      // (flapping "aparece/desaparece" en el panel del supervisor).
      // La desconexión real la detecta el scheduler de ausencia (heartbeat timeout,
      // ~5-7 min), así que este logout por cierre de ventana era redundante y
      // contraproducente.
    }

    // Flag to prevent repeated profile fetches on re-initialization.
    let profileLoadAttempted = false

    async function initialize() {
      // No hacer nada si no hay user - el router guard maneja la navegación
      if (token.value && !user.value && !profileLoadAttempted) {
        profileLoadAttempted = true
        try {
          await fetchProfile()
        } catch {
          // Ignore fetch errors - auth state stays with token
        }
      }
    }

    function initAuth() {
      if (token.value) {
        localStorage.setItem('token', token.value)
        // If the app reloads with a token already present, restart the heartbeat
        // and activity tracking so the backend knows this operator is still online.
        startHeartbeat()
        startActivityTracking()
      }
    }

    return {
      user,
      token,
      refreshToken,
      loading,
      isAuthenticated,
      isAdmin,
      isManager,
      isOperator,
      isGenerator,
      userFullName,
      login,
      fetchProfile,
      refreshAccessToken,
      changePassword,
      forgotPassword,
      resetPassword,
      loginWithOAuth,
      logout,
      initAuth,
      initialize,
    }
  },
  {
    persist: {
      key: 'cti-auth',
      paths: ['token', 'refreshToken', 'user'],
    },
  }
)
